/**
 * The exercises past the basics: conversation without a clearance, holds and approach clearances,
 * recovery after a go-around, and the advanced work where several aircraft want one runway.
 *
 * Positions come from the airport data so the numbers in the text match the scope. Checks read
 * flight state and sim events; every step's solution is replayed in the tests, so a sim change
 * that makes a lesson unpassable fails the build.
 */
import { newRequest, runwayUnable, type Sim } from '@atc/sim';
import { hdg3 } from '../hints';
import type { Lesson, StepContext, StepResult } from './lesson';
import { asked, backNm, everSawFor, find, flying, gapNm, gone, magneticVector, onFinal, pairIn, runway, sawFor, spoken } from './lesson-helpers';

/**
 * Place an arrival on the extended centreline, already pointed down the final. Its speed arrives
 * as one the previous sector assigned: without it every inbound accelerates to 250 knots within
 * half a minute, and the gaps a lesson sets up are not the gaps the player sees.
 */
const inbound = (sim: Sim, rw: ReturnType<typeof runway>, cs: string, type: string, nm: number, altitudeFt: number, iasKt: number, rightNm = 0) => {
  const p = onFinal(rw, nm, rightNm);
  const ac = sim.addAircraft({ kind: 'arrival', callsign: cs, type, lat: p.lat, lon: p.lon, altitudeFt, headingDeg: rw.headingDeg, iasKt });
  ac.targetIasKt = ac.assigned.speedKt = iasKt;
  return ac;
};

export const FIRST_CONTACT: Lesson = {
  id: 'first-contact', title: 'First contact',
  summary: 'Answer a call, buy yourself time, and ask for a repeat before you clear anything.',
  airport: 'llbg',
  position: 'approach',
  tier: 'beginner',
  commands: ['acknowledge', 'repeat', 'standby', 'altitude', 'direct'],
  briefing: {
    chartIds: ['approach-ils-rwy-12'],
    situation: 'Ben Gurion approach · landing 12',
    scene: [
      'ELY011 checks in on the GODED 2 arrival, 25 miles northwest at 7,000 ft. The request is clipped by radio interference; tower is still confirming which runway is available.',
      'Acknowledge the flight, ask for the missing words, then have it stand by while the runway report comes back. Only then give it a useful descent and route.',
    ],
    objectives: ['Tell ELY011 you have them on radar', 'Ask the crew to say their request again', 'Tell them to stand by', 'Then give the clearance'],
    notes: ['Radar contact, say again and stand by change nothing about the flight.', 'A crew told to stand by waits two minutes before pressing you again.'],
  },
  realism: 'low',
  build(airport) {
    const cs = 'ELY011', say = spoken(airport, cs);
    return {
      setup(sim) {
        // On the GODED 2, at DIRAN (7,000 ft is the top of the chart's window there), 25 miles northwest.
        const ac = sim.addAircraft({ kind: 'arrival', callsign: cs, type: 'B738', star: 'GODED2', at: 'DIRAN', altitudeFt: 7000, iasKt: 250 });
        // Patience is off in a lesson, so a check-in leaves nothing outstanding. Give the crew a
        // real request, otherwise there is nothing for "stand by" to defer.
        ac.pilot.request = newRequest('approach', sim.simTimeS);
      },
      steps: [
        {
          title: 'Radar contact',
          text: `${cs} has checked in, but its request broke up. “Radar contact” confirms that you have it without guessing at the clearance.`,
          hint: `${cs} RC`,
          say: `${say}, radar contact`,
          solution: [`${cs} RC`],
          onEnter: (sim) => sim.academyCall(cs, `${cs}, requesting the ... [transmission clipped]`, 'approach', `${cs}, request ILS runway 12 after GODED`),
          check: (ctx) => (ctx.radio.some((m) => m.callsign === cs && m.from === 'atc' && /radar contact/i.test(m.text)) ? 'pass' : find(ctx, cs) ? null : gone(ctx, cs)),
          testTimeoutS: 10,
        },
        {
          title: 'Say again',
          text: `The approach request was clipped. Ask ${cs} to repeat it; this time the crew says it wants the runway 12 ILS after GODED.`,
          hint: `${cs} SAYAGAIN`,
          say: `${say}, say again`,
          solution: [`${cs} SAYAGAIN`],
          check: (ctx) => (ctx.radio.some((m) => m.callsign === cs && m.from === 'pilot' && /request ILS runway 12 after GODED/i.test(m.text)) ? 'pass' : find(ctx, cs) ? null : gone(ctx, cs)),
          testTimeoutS: 10,
        },
        {
          title: 'Stand by',
          text: `Tower has not confirmed runway 12 yet. Tell ${cs} to stand by while you get the runway report.`,
          hint: `${cs} STANDBY`,
          say: `${say}, stand by`,
          solution: [`${cs} STANDBY`],
          check: (ctx) => {
            const a = find(ctx, cs);
            if (!a) return gone(ctx, cs);
            // The standby counter, not the deferral: a request the pilot raised and auto-deferred
            // is not something you said, and must not pass this step on the player's behalf.
            if ((a.pilot.request?.standbys ?? 0) > 0) return 'pass';
            // The clearance came first, so there is nothing left to defer. Nothing wrong with
            // answering a crew you are ready for; stand by is for when you are not.
            const answered = a.assigned.altitudeFt === 5000 && a.nav.mode === 'direct' && a.nav.fix === 'GODED';
            return answered ? { pass: `You had already answered ${cs}, so there was nothing left to defer. Stand by is what you say when the picture is not ready yet — it buys two minutes and beats silence.` } : null;
          },
          testTimeoutS: 10,
        },
        {
          title: 'Now answer it',
          text: `Tower confirms runway 12 is available. Descend ${cs} to 5,000 ft and send it to GODED on its requested route. You can chain both into one transmission.`,
          onEnter: (sim) => sim.academyCall(null, 'Ben Gurion tower: runway 12 available for arrivals.'),
          hint: `${cs} C 5 C GODED`,
          say: `${say}, descend and maintain 5,000, proceed direct GODED`,
          solution: [`${cs} C 5 C GODED`],
          check: (ctx) => {
            const a = find(ctx, cs);
            if (!a) return gone(ctx, cs);
            return a.assigned.altitudeFt === 5000 && a.nav.mode === 'direct' && a.nav.fix === 'GODED' ? 'pass' : null;
          },
          testTimeoutS: 20,
        },
      ],
    };
  },
};

export const APPROACH_AND_HOLD: Lesson = {
  id: 'approach-and-hold', title: 'Hold, approach, land',
  summary: 'Hold an arrival while tower lands a heavy, then build and transfer its approach.',
  airport: 'llbg',
  position: 'approach',
  tier: 'mid',
  commands: ['hold', 'approach', 'contactTower'],
  briefing: {
    chartIds: ['approach-ils-rwy-12'],
    situation: 'Ben Gurion approach · landing 12',
    scene: [
      'A heavy arrival is on short final for runway 12. Tower has not confirmed the runway free for the next aircraft, so ELY551 cannot be released straight into another approach.',
      'Hold ELY551 at GODED while the heavy lands. GODED is where the ILS 12 begins, so once tower reports the runway clear an approach clearance releases the arrival straight from the hold onto the chart. Tower will issue its landing clearance after your handoff.',
    ],
    objectives: ['Hold ELY551 behind the heavy', 'Wait for tower to report the heavy landed', 'Clear the ILS from the hold', 'Transfer ELY551 to tower and confirm touchdown'],
    notes: ['The GODED hold is published: inbound 116° on the chart, left turns, one-minute legs, 5,000 ft or above.', 'A hold protects the sequence, not just the runway. An ILS clearance is separate from a landing clearance.'],
  },
  realism: 'low',
  build(airport) {
    const cs = 'ELY551', say = spoken(airport, cs);
    const rw = runway(airport, '12');
    return {
      setup(sim) {
        const lead = inbound(sim, rw, 'ELY550', 'B77W', 2, 700, 150);
        sim.academyPrepareArrival('ELY550', '12');
        for (let i = 0; i < 120 && !lead.approach?.established; i++) sim.step(.25);
        sim.command('ELY550 CTW');
        // On the GODED 2, five miles before GODED at 5,000 ft, the lowest level the GODED hold allows.
        sim.addAircraft({ kind: 'arrival', callsign: cs, type: 'B738', star: 'GODED2', at: 'GODED', nmBefore: 5, altitudeFt: 5000, iasKt: 210 });
      },
      steps: [
        {
          title: 'Put it in the hold',
          text: `${cs} is closing behind a heavy. Hold it at GODED to keep it out of the stream until tower has the runway clear. The crew flies the published hold: left turns, on the far side of the final from the field.`,
          hint: `${cs} H GODED`,
          say: `${say}, hold at GODED as published`,
          solution: [`${cs} H GODED`],
          check: (ctx) => {
            const a = find(ctx, cs);
            if (!a) return gone(ctx, cs);
            return a.nav.mode === 'hold' && a.nav.hold?.fix === 'GODED' ? 'pass' : null;
          },
          testTimeoutS: 30,
        },
        {
          title: 'Wait for the heavy', text: 'Tower has the heavy on final. Keep ELY551 in its hold until touchdown; a free runway and a safe sequence are both needed.',
          hint: 'Watch the lead aircraft land.', say: 'Nothing to say; maintain the hold.', solution: [],
          check: (ctx) => everSawFor(ctx, 'touchdown', 'ELY550') ? 'pass' : find(ctx, 'ELY550') ? null : gone(ctx, 'ELY550'), testTimeoutS: 600,
        },
        {
          title: 'Clear the ILS from the hold',
          text: `The heavy has landed. Clear ${cs} for the ILS: from the hold the crew leaves GODED on the chart — 3,000 ft by BG055, 2,500 ft at the intermediate fix — and joins the localizer on the way. No vectors needed.`,
          hint: `${cs} I 12`,
          say: `${say}, cleared ILS approach runway 12`,
          solution: [`${cs} I 12`],
          check: (ctx) => {
            const a = find(ctx, cs);
            if (!a) return gone(ctx, cs);
            return a.approach ? 'pass' : null;
          },
          testTimeoutS: 30,
        },
        {
          title: 'Watch the ILS capture',
          text: `${cs} has the approach clearance. Watch it finish the hold at GODED, turn onto the final and capture the localizer.`,
          hint: 'Monitor the established indication.',
          say: 'The clearance was already issued; listen for established.',
          solution: [],
          check: (ctx) => {
            if (everSawFor(ctx, 'established', cs)) return 'pass';
            if (sawFor(ctx, 'missedIntercept', cs)) return { fail: `${cs} did not capture the localizer. Clear the approach while it is still in the hold at GODED.` };
            return find(ctx, cs) ? null : gone(ctx, cs);
          },
          testTimeoutS: 900,
        },
        {
          title: 'Hand it to tower',
          text: `${cs} is established and the heavy is down. Transfer the arrival to tower, which checks the runway and clears it to land.`,
          hint: `${cs} CTW`, say: `${say}, contact tower`, solution: [`${cs} CTW`],
          check: (ctx) => ctx.all.some((e) => e.type === 'academyTowerHandoff' && e.ac.callsign === cs) ? 'pass' : find(ctx, cs) ? null : gone(ctx, cs), testTimeoutS: 10,
        },
        {
          title: 'Complete the sequence', text: `Watch ${cs} land. You held it for a reason, then returned it to the approach when the traffic picture allowed.`,
          hint: 'Watch for touchdown.', say: 'Tower has the aircraft.', solution: [],
          check: (ctx) => everSawFor(ctx, 'touchdown', cs) ? 'pass' : sawFor(ctx, 'goAround', cs) ? { fail: 'The aircraft went around. Transfer earlier after a stable approach.' } : find(ctx, cs) ? null : gone(ctx, cs), testTimeoutS: 600,
        },
      ],
    };
  },
};

export const GO_AROUND_RECOVERY: Lesson = {
  id: 'go-around-recovery', title: 'After the go-around',
  summary: 'Receive a missed approach from tower, find out the crew’s plan, and vector it safely.',
  airport: 'llbg',
  position: 'approach',
  tier: 'mid',
  commands: ['query-intentions', 'heading', 'altitude', 'hold'],
  briefing: {
    chartIds: ['approach-ils-rwy-12'],
    situation: 'Ben Gurion approach · landing 12, runway about to be blocked',
    scene: [
      'Tower reports a vehicle on runway 12 and sends ELY951 around. The crew is climbing on runway heading when it returns to your frequency.',
      'The safety call was tower’s. Your work begins with a climbing aircraft, another inbound stream, and a crew waiting for a plan.',
    ],
    objectives: ['Receive tower’s go-around report', 'Ask the crew what they intend', 'Give a heading and a level in one call', 'Hold them clear while the runway is inspected'],
    notes: ['A go-around flown for safety costs no points.', 'After a go-around the aircraft keeps runway heading and climbs until you say otherwise.'],
  },
  realism: 'low',
  build(airport) {
    const cs = 'ELY951', say = spoken(airport, cs);
    const rw = runway(airport, '12');
    const away = hdg3(magneticVector(airport, rw.headingDeg + 180));
    const lost = (ctx: StepContext): StepResult => (find(ctx, cs) ? null : gone(ctx, cs));
    return {
      setup(sim) {
        const p = onFinal(rw, 3.5, 0);
        sim.addAircraft({ kind: 'arrival', callsign: cs, type: 'B738', lat: p.lat, lon: p.lon, altitudeFt: airport.elevationFt + 1200, headingDeg: rw.headingDeg, iasKt: 160 });
        sim.command(`${cs} I 12`);
      },
      steps: [
        {
          title: 'Receive the missed approach',
          text: `Tower has instructed ${cs} to go around because a vehicle is on runway 12. The aircraft has dropped its approach and is climbing on runway heading; you own its next route.`,
          hint: 'Listen to tower’s report and find the missed approach on the scope.',
          say: 'Tower has already issued the go-around.',
          solution: [],
          onEnter: (sim) => { sim.academyTowerGoAround(cs, 'vehicle on runway 12'); },
          check: (ctx) => {
            if (ctx.all.some((e) => e.type === 'academyCall' && e.text.includes('vehicle on runway')) && everSawFor(ctx, 'goAround', cs)) return 'pass';
            return lost(ctx);
          },
          testTimeoutS: 5,
        },
        {
          title: 'Ask what they want',
          text: `${cs} is climbing away with no routing. Before you invent a plan, ask for theirs: the crew may want another approach, or fuel may have made the choice for them.`,
          hint: `${cs} Q intentions`,
          say: `${say}, say intentions`,
          solution: [`${cs} Q intentions`],
          check: (ctx) => (asked(ctx, cs, 'intentions') ? 'pass' : lost(ctx)),
          testTimeoutS: 20,
        },
        {
          title: 'Give it a plan',
          text: `Turn ${cs} away from the final and give it a level, in one transmission. Until it has both it is still flying the missed approach and the crew will keep calling.`,
          hint: `${cs} C ${away} R C 4`,
          say: `${say}, turn right heading ${away}, climb and maintain 4,000`,
          solution: [`${cs} C ${away} R C 4`],
          check: (ctx) => {
            const a = find(ctx, cs);
            if (!a) return gone(ctx, cs);
            return a.assigned.altitudeFt === 4000 && a.assigned.headingDeg === Number(away) && !a.pilot.request && !a.pilot.recovery ? 'pass' : null;
          },
          testTimeoutS: 180,
        },
        {
          title: 'Park it',
          text: `You have a runway to sort out and an aircraft you do not want to keep vectoring. Hold ${cs} at GODED: it stays where you put it until you are ready for it.`,
          hint: `${cs} H GODED`,
          say: `${say}, hold at GODED as published`,
          solution: [`${cs} H GODED`],
          check: (ctx) => {
            const a = find(ctx, cs);
            if (!a) return gone(ctx, cs);
            return a.nav.mode === 'hold' && a.nav.hold?.fix === 'GODED' ? 'pass' : null;
          },
          testTimeoutS: 30,
        },
      ],
    };
  },
};

export const DEPARTURE_STREAM: Lesson = {
  id: 'departure-stream', title: 'Departure stream',
  summary: 'Receive three tower departures on one corridor and keep their climbs separated.',
  airport: 'llbg',
  position: 'approach',
  tier: 'pro',
  commands: ['altitude'],
  briefing: {
    chartIds: ['departure-sid-rwy-26'],
    situation: 'Ben Gurion approach · departing 26, three in the queue',
    scene: [
      'Tower is launching ISR821, ISR822 and ELY823 from runway 26, one after another. All three fly the SUVAS 1E to KESEF, the same departure corridor.',
      'Tower owns each takeoff and the crews fly the SID. You receive each aircraft airborne and must give it its own level before the next one climbs up the same track.',
    ],
    objectives: ['Climb the first departure', 'Receive and separate the second', 'Receive and separate the third', 'Get all three handed off without a conflict'],
    notes: ['Tower waits for runway clearance between launches.', 'Departures are handed off within a mile of KESEF at 4,000 ft above the field or higher.'],
  },
  realism: 'low',
  build(airport) {
    const first = 'ISR821', second = 'ISR822', third = 'ELY823', exit = 'KESEF';
    const sayFirst = spoken(airport, first), saySecond = spoken(airport, second), sayThird = spoken(airport, third);
    const waitStep = (cs: string, ahead: string) => ({
      title: `Receive ${cs} from tower`,
      text: `Tower holds ${cs} until ${ahead} has cleared the runway, then launches it into your departure corridor. Read its level and trend before issuing the next climb.`,
      hint: 'Watch the tower handoff and the two outbound tracks.',
      say: 'Tower launches the next aircraft; no takeoff clearance from approach.',
      solution: [] as string[],
      onEnter(sim: Sim) {
        const ac = sim.addAircraft({ kind: 'departure', callsign: cs, type: cs === second ? 'A320' : 'B738', runway: '26', requested: exit });
        for (let i = 0; i < 800 && runwayUnable(airport, sim.internal(), ac, { kind: 'takeoff' }); i++) sim.step(.25);
        sim.academyLaunchDeparture(cs, 3000);
        for (let i = 0; i < 480 && ac.onGround; i++) sim.step(.25);
        sim.academyCall(null, `Ben Gurion tower: ${cs} airborne runway 26, transferred to departure control.`);
      },
      check: (ctx: StepContext): StepResult => (find(ctx, cs) && !find(ctx, cs)!.onGround ? 'pass' : find(ctx, cs) ? null : gone(ctx, cs)),
      testTimeoutS: 30,
    });
    return {
      setup(sim) {
        const ac = sim.addAircraft({ kind: 'departure', callsign: first, type: 'A320', runway: '26', requested: exit });
        sim.academyLaunchDeparture(first, 3000);
        for (let i = 0; i < 480 && ac.onGround; i++) sim.step(.25);
      },
      steps: [
        {
          title: 'Climb the first',
          text: `Tower has launched ${first} on the SUVAS 1E. Give it 5,000 ft: high enough at ${exit} for the onward handoff, and the lowest of the three levels you will hand out.`,
          hint: `${first} C 5`,
          say: `${sayFirst}, climb and maintain 5,000`,
          solution: [`${first} C 5`],
          check: (ctx) => {
            const a = find(ctx, first);
            if (!a) return gone(ctx, first);
            return a.assigned.altitudeFt === 5000 ? 'pass' : null;
          },
          testTimeoutS: 120,
        },
        waitStep(second, first),
        {
          title: 'Separate the second climb',
          text: `${second} follows ${first} up the same SID. Give it 6,000 ft, a thousand feet above, then watch their changing vertical gap.`,
          hint: `${second} C 6`,
          say: `${saySecond}, climb and maintain 6,000`,
          solution: [`${second} C 6`],
          check: (ctx) => {
            const a = find(ctx, second);
            if (!a) return gone(ctx, second);
            return a.assigned.altitudeFt === 6000 ? 'pass' : null;
          },
          testTimeoutS: 120,
        },
        waitStep(third, second),
        {
          title: 'Separate the third climb',
          text: `Last tower handoff: ${third} joins the outbound line. Assign 7,000 ft, the SUVAS 1E's top below ORLEV's 8,000 ft limit, then monitor all three tracks.`,
          hint: `${third} C 7`,
          say: `${sayThird}, climb and maintain 7,000`,
          solution: [`${third} C 7`],
          check: (ctx) => {
            const a = find(ctx, third);
            if (!a) return gone(ctx, third);
            return a.assigned.altitudeFt === 7000 ? 'pass' : null;
          },
          testTimeoutS: 120,
        },
        {
          title: 'All three away',
          text: `Nothing left to say. Watch the three of them run out to ${exit} and hand off in order. If your levels were right, none of them ever came within a thousand feet of another.`,
          hint: 'No command. Wait for the three handoffs.',
          say: 'Nothing to say. Watch the handoffs.',
          solution: [],
          check: (ctx) => {
            if (ctx.events.some((e) => e.type === 'conflictStart')) return { fail: 'Two departures lost separation on the way out. Give each one its own level before it rolls.' };
            if (ctx.events.some((e) => e.type === 'exited')) return { fail: `A departure left the airspace without a handoff. Leave each on its SID to ${exit}, high enough for the handoff.` };
            return ctx.sim.stats().handoffs >= 3 ? 'pass' : null;
          },
          testTimeoutS: 1500,
        },
      ],
    };
  },
};

export const WAKE_SPACING: Lesson = {
  id: 'wake-spacing', title: 'Behind the heavy',
  summary: 'A heavy on final needs five miles behind it, and speed alone will not buy them.',
  airport: 'llbg',
  position: 'approach',
  tier: 'pro',
  commands: ['speed', 'hold', 'altitude', 'approach', 'contactTower'],
  briefing: {
    chartIds: ['approach-ils-rwy-12'],
    situation: 'Ben Gurion approach · landing 12, heavy on final',
    scene: [
      'ELY901, a 777, is eight miles out on the runway 12 final and with tower. AIZ902, an E195, is seven miles behind it and closing fast; the crew requests a slower speed.',
      'A heavy leaves turbulence behind it that the scope does not draw. Behind one, a medium aircraft needs five miles, not three — and once a follower is established on the localizer the sim flies it at the same speed as everyone else, so slowing it down stops the gap shrinking but never opens it.',
    ],
    objectives: ['Spot the closing wake gap', 'Slow and hold the follower clear', 'Land the heavy', 'Rebuild and complete the follower’s approach'],
    notes: ['Five miles behind a heavy, for a medium aircraft.', 'Wake separation is enforced in this exercise whatever your realism setting.'],
  },
  realism: 'low',
  // The wake rule is off at low realism, so without this the alert the lesson teaches never fires.
  realismOverrides: { wakeSeparation: true },
  build(airport) {
    const lead = 'ELY901', follow = 'AIZ902';
    const sayFollow = spoken(airport, follow);
    const rw = runway(airport, '12');
    return {
      setup(sim) {
        const heavy = inbound(sim, rw, lead, 'B77W', 8, 2700, 160);
        sim.command(`${lead} I 12`);
        for (let i = 0; i < 120 && !heavy.approach?.established; i++) sim.step(.25);
        sim.command(`${lead} CTW`);
        inbound(sim, rw, follow, 'E195', 15, 3000, 250);
      },
      steps: [
        {
          title: 'The wake alert',
          text: `${follow} asks to slow behind the heavy. It is seven miles back at 250 knots and needs at least five miles; watch how quickly the gap changes before answering.`,
          onEnter: (sim) => sim.academyCall(follow, `${follow}, request 180 knots behind the heavy`),
          hint: 'No command yet. Watch the two blocks and the gap on the final.',
          say: 'Nothing to say yet. Watch the spacing.',
          solution: [],
          check: (ctx) => {
            if (ctx.events.some((e) => e.type === 'conflictWarning' && pairIn(e, lead, follow))) return 'pass';
            const a = find(ctx, lead), b = find(ctx, follow);
            if (!a || !b) return gone(ctx, a ? follow : lead);
            // A geometric fallback, so the lesson still teaches the gap if the alert is ever retuned.
            return gapNm(rw, a, b) < 5 ? 'pass' : null;
          },
          testTimeoutS: 300,
        },
        {
          title: 'Stop the closure',
          text: `First, stop it getting worse. Bring ${follow} back to 180 knots. This buys time — but watch what it does not do: both aircraft are on the same approach profile, so slowing the follower halts the closure without opening the gap back up.`,
          hint: `${follow} S 180`,
          say: `${sayFollow}, reduce speed to 180 knots`,
          solution: [`${follow} S 180`],
          check: (ctx) => {
            const a = find(ctx, follow);
            if (!a) return gone(ctx, follow);
            if (a.assigned.speedKt === 180) return 'pass';
            // Taken off the approach already: the speed reduction it skipped was only ever a way
            // to buy time for exactly this, so there is nothing left for it to buy.
            return a.nav.mode === 'hold' ? { pass: `${follow} is already out of the sequence, so there was no closure left to stop. Slowing a follower buys time; it never opens a gap back up.` } : null;
          },
          testTimeoutS: 30,
        },
        {
          title: 'Take it out of the sequence',
          text: `Speed alone will not give you five miles back. The only way out is to stop ${follow} being on this approach at all: hold it at GODED and climb it to 5,000 ft, clear of the heavy and its wake.`,
          hint: `${follow} H GODED C 5`,
          say: `${sayFollow}, climb and maintain 5,000, hold at GODED as published`,
          solution: [`${follow} H GODED C 5`],
          check: (ctx) => {
            const a = find(ctx, follow);
            if (!a) return gone(ctx, follow);
            return a.nav.mode === 'hold' && a.nav.hold?.fix === 'GODED' && a.assigned.altitudeFt === 5000 ? 'pass' : null;
          },
          testTimeoutS: 30,
        },
        {
          title: 'Clear of the wake',
          text: `${follow} is turning away and climbing. Watch both alerts go out: the gap is no longer the thing keeping them safe, the thousand feet is.`,
          hint: 'No command. Wait for both alerts to clear.',
          say: 'Nothing to say. Watch the alerts go out.',
          solution: [],
          check: (ctx) => {
            const a = find(ctx, lead), b = find(ctx, follow);
            if (!a) return gone(ctx, lead);
            if (!b) return gone(ctx, follow);
            return ctx.elapsedS >= 20 && !a.conflictWarning && !b.conflictWarning && !a.conflict && !b.conflict ? 'pass' : null;
          },
          testTimeoutS: 300,
        },
        {
          title: 'Land the heavy',
          text: `${lead} was cleared to land before any of this started, and nothing you did touched it. Watch it down. Protecting the aircraft behind never meant disturbing the one in front.`,
          hint: 'No command. Watch the heavy land.',
          say: 'Nothing to say. Watch it touch down.',
          solution: [],
          check: (ctx) => {
            if (everSawFor(ctx, 'touchdown', lead)) return 'pass';
            if (sawFor(ctx, 'goAround', lead)) return { fail: `${lead} went around. The heavy's approach should have been left alone.` };
            return find(ctx, lead) ? null : gone(ctx, lead);
          },
          testTimeoutS: 600,
        },
        {
          title: 'Clear it from the hold',
          text: `The heavy has landed. Clear ${follow} for the ILS from the GODED hold: the crew leaves the hold on the chart, down to 3,000 ft by BG055, and joins the localizer with the heavy's wake long gone.`,
          hint: `${follow} I 12`, say: `${sayFollow}, cleared ILS approach runway 12`, solution: [`${follow} I 12`],
          check: (ctx) => everSawFor(ctx, 'established', follow) ? 'pass' : sawFor(ctx, 'missedIntercept', follow) ? { fail: 'The follower did not capture the localizer. Clear the approach while it is still in the GODED hold.' } : find(ctx, follow) ? null : gone(ctx, follow), testTimeoutS: 900,
        },
        {
          title: 'Hand it to tower', text: `The wake gap is no longer an issue. Transfer ${follow} once established and monitor its landing.`,
          hint: `${follow} CTW`, say: `${sayFollow}, contact tower`, solution: [`${follow} CTW`],
          check: (ctx) => ctx.all.some((e) => e.type === 'academyTowerHandoff' && e.ac.callsign === follow) ? 'pass' : find(ctx, follow) ? null : gone(ctx, follow), testTimeoutS: 10,
        },
        {
          title: 'Complete both arrivals', text: `Confirm ${follow} touches down after the heavy. The earlier hold cost time but restored a safe sequence.`,
          hint: 'Watch for touchdown.', say: 'Tower has the aircraft.', solution: [],
          check: (ctx) => everSawFor(ctx, 'touchdown', follow) ? 'pass' : sawFor(ctx, 'goAround', follow) ? { fail: 'The follower went around; set up an earlier, stable intercept.' } : find(ctx, follow) ? null : gone(ctx, follow), testTimeoutS: 900,
        },
      ],
    };
  },
};

export const EMERGENCY_PRIORITY: Lesson = {
  id: 'emergency-priority', title: 'Priority flight',
  summary: 'A PAN call from behind the traffic: clear the approach and land it first.',
  airport: 'llbg',
  position: 'approach',
  tier: 'pro',
  commands: ['acknowledge', 'query-personsOnBoard', 'altitude', 'heading', 'direct', 'approach', 'contactTower'],
  briefing: {
    chartIds: ['approach-ils-rwy-12', 'arrival-llbg-standard-arrival-chart-instrument-rnav-star-icao-rwys-12-30-goded2-ninet1'],
    situation: 'Ben Gurion approach · landing 12, PAN inbound',
    scene: [
      'ELY701 is twelve miles out and already cleared for the runway 12 approach. ELY702 is behind it on the GODED 2 arrival, at DIRAN — and has just declared a medical emergency.',
      'The aircraft with the problem is behind the one without it. Priority does not mean landing it in turn; it means rebuilding the sequence around it, which costs the other aircraft its approach.',
    ],
    objectives: ['Acknowledge the emergency and ask how many people are aboard', 'Take the aircraft ahead off the approach', 'Clear the emergency for the ILS', 'Transfer it to tower and confirm landing', 'Bring the displaced arrival back via GODED'],
    notes: ['Any transmission to the aircraft counts as acknowledging the call.', 'Souls on board is what the emergency services ask you for first.'],
  },
  realism: 'low',
  build(airport) {
    const ahead = 'ELY701', pan = 'ELY702';
    const sayAhead = spoken(airport, ahead), sayPan = spoken(airport, pan);
    const rw = runway(airport, '12');
    return {
      setup(sim) {
        inbound(sim, rw, ahead, 'B738', 12, 3000, 180);
        sim.command(`${ahead} I 12`);
        sim.addAircraft({ kind: 'arrival', callsign: pan, type: 'A320', star: 'GODED2', at: 'DIRAN', altitudeFt: 6000, iasKt: 230 });
        // Medical: a PAN with no performance penalty, so the clearances below are not fought.
        sim.declareEmergency(pan, 'medical');
      },
      steps: [
        {
          title: 'Acknowledge, then ask',
          text: `${pan} has declared. Tell them you have it, then ask for souls on board — the number the emergency services will want before the aircraft is on the ground. Neither call changes the flight.`,
          hint: `${pan} RC, then ${pan} Q personsOnBoard`,
          say: `${sayPan}, radar contact — then: ${sayPan}, say souls on board`,
          solution: [`${pan} RC`, `${pan} Q personsOnBoard`],
          check: (ctx) => {
            const a = find(ctx, pan);
            if (!a) return gone(ctx, pan);
            return asked(ctx, pan, 'personsOnBoard') && a.emergency?.acknowledgedAtS !== undefined ? 'pass' : null;
          },
          testTimeoutS: 20,
        },
        {
          title: 'Clear the approach',
          text: `${ahead} is on the approach the emergency needs. Take it off: turn it left to 030, away from ${pan} on the other side of the final, and climb it to 5,000 ft. It loses its approach clearance the moment you turn it — that is the cost, and it is the right one.`,
          hint: `${ahead} C 030 L C 5`,
          say: `${sayAhead}, turn left heading zero three zero, climb and maintain 5,000`,
          solution: [`${ahead} C 030 L C 5`],
          check: (ctx) => {
            const a = find(ctx, ahead);
            if (!a) return gone(ctx, ahead);
            return !a.approach && (a.assigned.altitudeFt ?? 0) >= 4000 ? 'pass' : null;
          },
          testTimeoutS: 30,
        },
        {
          title: 'Clear the emergency approach',
          text: `The final is yours to give. Clear ${pan} for the ILS on its arrival: the crew flies the GODED 2 down onto the approach, the fastest way in. Tower is ready to receive it once established.`,
          hint: `${pan} I 12`,
          say: `${sayPan}, cleared ILS runway 12 approach`,
          solution: [`${pan} I 12`],
          check: (ctx) => {
            if (everSawFor(ctx, 'established', pan)) return 'pass';
            if (sawFor(ctx, 'missedIntercept', pan)) return { fail: `${pan} did not capture the localizer. Clear the ILS while it is still on its arrival.` };
            if (sawFor(ctx, 'goAround', pan)) return { fail: `${pan} went around. An emergency should not be sent round; give it the clearances earlier.` };
            return find(ctx, pan) ? null : gone(ctx, pan);
          },
          testTimeoutS: 900,
        },
        {
          title: 'Hand priority to tower', text: `The emergency is established. Transfer ${pan} to tower; it will issue the landing clearance and coordinate the runway response.`,
          hint: `${pan} CTW`, say: `${sayPan}, contact tower`, solution: [`${pan} CTW`],
          check: (ctx) => ctx.all.some((e) => e.type === 'academyTowerHandoff' && e.ac.callsign === pan) ? 'pass' : find(ctx, pan) ? null : gone(ctx, pan), testTimeoutS: 10,
        },
        {
          title: 'Let the emergency pass',
          text: `Keep ${ahead} heading away until ${pan} is inside 12 miles on the final. Sent back to GODED now, it would be between the emergency and the runway.`,
          hint: `No command yet. Watch ${pan} come down the final.`,
          say: 'Nothing to say yet.',
          solution: [],
          check: (ctx) => {
            const p = find(ctx, pan);
            if (!p) return everSawFor(ctx, 'touchdown', pan) ? 'pass' : gone(ctx, pan);
            return backNm(rw, p) <= 12 ? 'pass' : find(ctx, ahead) ? null : gone(ctx, ahead);
          },
          testTimeoutS: 600,
        },
        {
          title: 'Resequence the displaced arrival',
          text: `${pan} is well down the final. Bring ${ahead} back behind it the way it would have come in: direct GODED, cleared ILS. The crew flies to GODED at 5,000 ft and joins the approach from there, well behind the emergency.`,
          hint: `${ahead} C GODED I 12`,
          say: `${sayAhead}, proceed direct GODED, cleared ILS approach runway 12`,
          solution: [`${ahead} C GODED I 12`],
          check: (ctx) => {
            if (everSawFor(ctx, 'established', ahead) && ctx.sim.find(ahead)?.approach) return 'pass';
            if (sawFor(ctx, 'missedIntercept', ahead)) return { fail: `${ahead} did not capture the localizer. Send it direct GODED with the approach clearance, so it joins the chart there.` };
            return find(ctx, ahead) ? null : gone(ctx, ahead);
          },
          testTimeoutS: 900,
        },
        {
          title: 'Confirm priority landing', text: `Watch ${pan} touch down ahead of the displaced arrival.`,
          hint: 'Watch for touchdown.', say: 'Tower has the aircraft.', solution: [],
          check: (ctx) => everSawFor(ctx, 'touchdown', pan) && ctx.sim.stats().emergencyLandings >= 1 ? 'pass' : ctx.all.some((e) => e.type === 'goAround' && e.ac.callsign === pan) ? { fail: `${pan} went around; check spacing and runway availability before handoff.` } : find(ctx, pan) ? null : gone(ctx, pan), testTimeoutS: 900,
        },
        {
          title: 'Hand the second one over', text: `${ahead} is established behind the emergency. Transfer it to tower and watch it land: the sequence is whole again.`,
          hint: `${ahead} CTW`, say: `${sayAhead}, contact tower`, solution: [`${ahead} CTW`],
          check: (ctx) => everSawFor(ctx, 'touchdown', ahead) ? 'pass' : sawFor(ctx, 'goAround', ahead) ? { fail: `${ahead} went around. Give it more room behind the emergency.` } : find(ctx, ahead) ? null : gone(ctx, ahead), testTimeoutS: 900,
        },
      ],
    };
  },
};

export const ARRIVAL_BANK: Lesson = {
  id: 'arrival-bank', title: 'Arrival bank',
  summary: 'Four inbounds begin safely spaced, then one faster follower threatens the heavy’s wake gap.',
  airport: 'llbg',
  position: 'approach',
  tier: 'pro',
  commands: ['altitude', 'speed', 'hold', 'approach', 'contactTower'],
  briefing: {
    chartIds: ['approach-ils-rwy-12'],
    situation: 'Ben Gurion approach · landing 12, four inbound',
    scene: [
      'Four aircraft are inbound for runway 12: ELY811 at ten miles, the heavy ELY812 at sixteen, AIZ813 at twenty-two and ISR814 at twenty-eight.',
      'The gaps begin legal, but AIZ813 is 30 knots faster than the heavy ahead. Its crew requests to keep that speed for now. If you agree without resequencing, the wake gap disappears.',
    ],
    objectives: ['Find the closing pair', 'Break the wake risk with a level and speed', 'Take the follower out of the sequence', 'Transfer the other three to tower and see them land'],
    notes: ['Five miles behind a heavy, three between everyone else.', 'Wake separation is enforced in this exercise whatever your realism setting.'],
  },
  realism: 'low',
  realismOverrides: { wakeSeparation: true },
  build(airport) {
    const one = 'ELY811', heavy = 'ELY812', close = 'AIZ813', last = 'ISR814';
    const sayClose = spoken(airport, close);
    const sayOne = spoken(airport, one), sayHeavy = spoken(airport, heavy), sayLast = spoken(airport, last);
    const rw = runway(airport, '12');
    return {
      setup(sim) {
        // All on the centreline, already pointed down the final: an approach clearance establishes
        // at once, so the gaps set here are the gaps the exercise is about.
        inbound(sim, rw, one, 'B738', 10, 3000, 180);
        inbound(sim, rw, heavy, 'B77W', 16, 3000, 180);
        inbound(sim, rw, close, 'E195', 22, 3000, 210);
        inbound(sim, rw, last, 'A320', 28, 3000, 180);
        for (const cs of [one, heavy, last]) sim.command(`${cs} I 12`);
      },
      steps: [
        {
          title: 'Read the bank',
          text: `${close} begins six miles behind ${heavy}, but is 30 knots faster and asks to keep its speed. Identify the closing pair before the wake alert becomes a loss of spacing.`,
          onEnter: (sim) => sim.academyCall(close, `${close}, request to keep 210 knots for now`),
          hint: 'No command yet. Watch the gap between the heavy and the aircraft behind it.',
          say: 'Nothing to say yet. Read the spacing down the final.',
          solution: [],
          check: (ctx) => {
            if (ctx.events.some((e) => e.type === 'conflictWarning' && pairIn(e, heavy, close))) return 'pass';
            const a = find(ctx, heavy), b = find(ctx, close);
            if (!a || !b) return gone(ctx, a ? close : heavy);
            return gapNm(rw, a, b) < 5.5 ? 'pass' : null;
          },
          testTimeoutS: 300,
        },
        {
          title: 'Break it with a level',
          text: `Wake only matters when the follower is below the aircraft ahead. Climb ${close} to 5,000 ft and slow it to 180 knots: two thousand feet above the bank, and no longer catching anybody.`,
          hint: `${close} C 5 S 180`,
          say: `${sayClose}, climb and maintain 5,000, reduce speed to 180 knots`,
          solution: [`${close} C 5 S 180`],
          check: (ctx) => {
            const a = find(ctx, close);
            if (!a) return gone(ctx, close);
            return a.assigned.altitudeFt === 5000 && a.assigned.speedKt === 180 && ctx.elapsedS >= 40 && !a.conflict && !a.conflictWarning ? 'pass' : null;
          },
          testTimeoutS: 240,
        },
        {
          title: 'Take it out of the line',
          text: `${close} is safe but it is still in the way of the three behind it. Hold it at GODED as published — at 5,000 ft, two thousand feet above the stream passing underneath, with the pattern off to the side of the final, and exactly where its approach will begin when the bank is down.`,
          hint: `${close} H GODED`,
          say: `${sayClose}, hold at GODED as published`,
          solution: [`${close} H GODED`],
          check: (ctx) => {
            const a = find(ctx, close);
            if (!a) return gone(ctx, close);
            return a.nav.mode === 'hold' && a.nav.hold?.fix === 'GODED' ? 'pass' : null;
          },
          testTimeoutS: 30,
        },
        {
          title: 'Transfer the remaining stream',
          text: `Three arrivals remain on the ILS, with the follower held clear. Transfer each established arrival to tower; tower will issue each landing clearance.`,
          hint: `${one} CTW, ${heavy} CTW, ${last} CTW`,
          say: `${sayOne}, contact tower — then ${sayHeavy} and ${sayLast}`,
          solution: [`${one} CTW`, `${heavy} CTW`, `${last} CTW`],
          check: (ctx) => {
            if (ctx.events.some((e) => e.type === 'conflictStart')) return { fail: 'Two aircraft in the bank lost separation. Break the wake pair before you start clearing the stream.' };
            if (ctx.events.some((e) => e.type === 'goAround')) return { fail: 'One of the stream went around. Clear each aircraft while it is still well out on the final.' };
            return ctx.sim.stats().landings >= 3 ? 'pass' : null;
          },
          testTimeoutS: 1500,
        },
      ],
    };
  },
};
